import { useEffect, useState } from 'react';
import {
  getCurrentAvatarFactoryDraft,
  type AvatarFactoryDraftDetail,
} from '../lib/admin-avatar-factory-api';
import SourceSetupTab from '../components/avatar-factory/SourceSetupTab';
import IdentityEditorTab from '../components/avatar-factory/IdentityEditorTab';
import PreviewChatPanel from '../components/avatar-factory/PreviewChatPanel';
import {
  AVATAR_FACTORY_COPY,
  translateAvatarFactoryStatus,
  type AvatarFactoryLanguage,
} from '../components/avatar-factory/i18n';
import type { FactoryTab } from '../components/avatar-factory/types';
import { useAdminAuthStore } from '../stores/useAdminAuthStore';
import './LegacyAvatarFactoryPage.css';

function formatLoadError(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

function getPublicGateMessage(language: AvatarFactoryLanguage): string {
  if (language === 'zh') {
    return '\u529f\u80fd\u5f00\u53d1\u4e2d\uff0c\u656c\u8bf7\u671f\u5f85';
  }
  return 'Feature development in progress. Stay tuned.';
}

export default function AvatarFactoryPage() {
  const accessToken = useAdminAuthStore((state) => state.accessToken);
  const [draft, setDraft] = useState<AvatarFactoryDraftDetail | null>(null);
  const [activeTab, setActiveTab] = useState<FactoryTab>('source');
  const [language, setLanguage] = useState<AvatarFactoryLanguage>('zh');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const copy = AVATAR_FACTORY_COPY[language];
  const isAccessGated = !accessToken;
  const publicGateMessage = getPublicGateMessage(language);

  const refreshDraft = async () => {
    if (!accessToken || !draft) {
      return;
    }
    const currentDraft = await getCurrentAvatarFactoryDraft(accessToken);
    if (currentDraft.id === draft.id) {
      setDraft(currentDraft);
    }
  };

  useEffect(() => {
    if (!accessToken) {
      setDraft(null);
      setIsLoading(false);
      setLoadError(null);
      return;
    }

    let ignore = false;
    setIsLoading(true);
    setLoadError(null);

    void getCurrentAvatarFactoryDraft(accessToken)
      .then((currentDraft) => {
        if (ignore) {
          return;
        }
        setDraft(currentDraft);
      })
      .catch((error) => {
        if (ignore) {
          return;
        }
        setDraft(null);
        setLoadError(formatLoadError(error, copy.page.loadFallback));
      })
      .finally(() => {
        if (!ignore) {
          setIsLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [accessToken, copy.page.loadFallback, copy.page.missingSession, reloadNonce]);

  return (
    <main className="avatar-factory-page">
      <div className="avatar-factory-shell">
        <header className="avatar-factory-header">
          <div>
            <p className="avatar-factory-eyebrow">{copy.page.eyebrow}</p>
            <h1>{copy.page.title}</h1>
            <p>{copy.page.description}</p>
          </div>

          <div className="avatar-factory-header-actions">
            <button
              className="avatar-factory-language-toggle"
              type="button"
              onClick={() => {
                setLanguage((current) => (current === 'zh' ? 'en' : 'zh'));
              }}
            >
              {copy.languageToggle}
            </button>

            {draft ? (
              <div className="avatar-factory-header-card" aria-label={copy.page.currentDraftStatus}>
                <span>{copy.page.currentDraft}</span>
                <strong>
                  {copy.page.draftPrefix}
                  {draft.displayName}
                </strong>
                <small>
                  {translateAvatarFactoryStatus(copy, draft.generationStatus)} · {copy.page.revisionPrefix}{' '}
                  {draft.previewRevision}
                </small>
              </div>
            ) : null}
          </div>
        </header>

        {isAccessGated ? (
          <>
            <div className="avatar-factory-public-preview" aria-hidden="true">
              <section className="avatar-factory-public-card">
                <span>{copy.source.title}</span>
                <strong>PDF / DOCX / Markdown</strong>
                <p>{copy.source.uploadHint}</p>
              </section>
              <section className="avatar-factory-public-card">
                <span>{copy.identity.title}</span>
                <strong>{copy.identity.layers.soul}</strong>
                <p>{copy.identity.layers.style} / {copy.identity.layers.memory} / {copy.identity.layers.skill}</p>
              </section>
              <section className="avatar-factory-public-card">
                <span>{copy.preview.title}</span>
                <strong>{copy.preview.agentSpeaker}</strong>
                <p>{copy.preview.placeholder}</p>
              </section>
            </div>
            <div className="avatar-factory-access-gate" role="status" aria-live="polite">
              <div className="avatar-factory-access-gate-card">
                <p>{publicGateMessage}</p>
              </div>
            </div>
          </>
        ) : null}

        {!isAccessGated && isLoading ? <p className="avatar-factory-status">{copy.page.loading}</p> : null}

        {!isAccessGated && loadError ? (
          <div className="avatar-factory-error" role="alert">
            <p>{loadError}</p>
            <button
              type="button"
              onClick={() => {
                setReloadNonce((value) => value + 1);
              }}
            >
              {copy.page.retry}
            </button>
          </div>
        ) : null}

        {!isAccessGated && !isLoading && draft && accessToken ? (
          <div className="avatar-factory-workbench">
            <section className="avatar-factory-left" aria-label={copy.page.editorAria}>
              <div className="avatar-factory-left-topline">
                <div>
                  <p className="avatar-factory-panel-kicker">{copy.page.build}</p>
                  <h2>{copy.page.draftControls}</h2>
                </div>
                <div className="avatar-factory-tabs" aria-label={copy.page.sectionsAria}>
                  <button
                    type="button"
                    aria-pressed={activeTab === 'source'}
                    className={activeTab === 'source' ? 'is-active' : ''}
                    onClick={() => setActiveTab('source')}
                  >
                    {copy.page.sourceTab}
                  </button>
                  <button
                    type="button"
                    aria-pressed={activeTab === 'identity'}
                    className={activeTab === 'identity' ? 'is-active' : ''}
                    onClick={() => setActiveTab('identity')}
                  >
                    {copy.page.identityTab}
                  </button>
                </div>
              </div>

              <section className="avatar-factory-tab-panel" aria-live="polite">
                {activeTab === 'source' ? (
                  <SourceSetupTab
                    accessToken={accessToken}
                    copy={copy}
                    draft={draft}
                    onDraftChange={setDraft}
                    onError={setLoadError}
                  />
                ) : (
                  <IdentityEditorTab
                    accessToken={accessToken}
                    copy={copy}
                    draft={draft}
                    onDraftChange={setDraft}
                    onError={setLoadError}
                  />
                )}
              </section>
            </section>

            <aside aria-label={copy.preview.aria} className="avatar-factory-preview">
              <PreviewChatPanel
                accessToken={accessToken}
                copy={copy}
                draft={draft}
                onDraftRefresh={refreshDraft}
                onError={setLoadError}
              />
            </aside>
          </div>
        ) : null}
      </div>
    </main>
  );
}
